import React, { useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const EmployeeLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: "/employee", label: "My Profile", icon: "👤", visible: true },
    {
      path: "/employee/e-form",
      label: "Personal Data Form",
      icon: "📋",
      visible: !user.filledForms.includes("Personal"),
      isForm: true
    },
    {
      path: "/employee/joining-report",
      label: "Joining Report",
      icon: "📊",
      visible: !user.filledForms.includes("Joining"),
      isForm: true
    },
    {
      path: "/employee/form-f",
      label: "Gratuity Nomination",
      icon: "💰",
      visible: !user.filledForms.includes("Form F"),
      isForm: true
    },
    {
      path: "/employee/form-11",
      label: "EPF Form 11",
      icon: "📄",
      visible: !user.filledForms.includes("EPF Form 11"),
      isForm: true
    },
    {
      path: "/employee/id-card",
      label: "ID Card",
      icon: "🆔",
      visible: !user.filledForms.includes("ID Card"),
      isForm: true
    },
    {
      path: `/employee/edit/${user?.eid}`,
      label: "Update Profile",
      icon: "📝",
      visible: !user.isNew,
      isForm: false
    },
    {
      path: `/employee/document-access/${user?.eid}`,
      label: "View Document",
      icon: "🔒",
      visible: !user.isNew,
      isForm: false
    },
    {
      path: `/employee/documents-upload/${user?.eid}`,
      label: "Documents Upload",
      icon: "📄",
      visible: true,
      isForm: false
    }
  ];

  return (
    <div className="flex min-h-screen max-h-screen overflow-y-auto bg-gray-50">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white shadow-xl
        transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 transition-transform duration-300 ease-in-out
        flex flex-col
      `}
      >
        {/* Logo and Brand Section */}
        <div className="px-6 py-2 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-3 mb-1">
            {/* Logo placeholder - replace with your actual logo */}
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="Kajaria"
              className="w-16 h-6 rounded-lg"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/gresbond-logo.png`}
              alt="Gresbond"
              className="filter dark-blue h-6 "
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/kerovit_logo.png`}
              alt="Kerovit"
              className="filter dark-blue h-6 m-1"
            />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-bold bg-gradient-to-r from-gray-600 to-green-300 bg-clip-text text-transparent">
              Employee Portal
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">
                {user?.username?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {user?.username?.toUpperCase() || "User"}
              </p>
              {/* <p className="text-xs text-gray-500">Employee</p> */}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-auto">
          {navItems
            .filter((item) => item.visible)
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`
                flex items-center space-x-3 p-3 rounded-lg transition-all duration-200
                ${
                  isActive(item.path)
                    ? "bg-blue-50 text-blue-700 border-r-4 border-blue-500"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }
              `}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
            <div className="mt-4"></div>
            {navItems.filter((item)=>!item.visible&&item.isForm).length>0&&<span className="text-xl font-bold text-blue-600 border-b border-gray-200 m-4">Submited Forms:- </span>}
            {
              navItems.filter((item)=>!item.visible&&item.isForm).map((item)=>(
                <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`
                flex items-center space-x-3 p-3 rounded-lg transition-all duration-200
                ${
                  isActive(item.path)
                    ? "bg-blue-50 text-blue-700 border-r-4 border-blue-500"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }
              `}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </Link>
              ))
            }
        </nav>

        {/* User Actions */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          <button className="w-full flex items-center space-x-3 p-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
            <span>⚙️</span>
            <span>Settings</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="flex items-center space-x-2">
              <img
                src="/images/logo.png"
                alt="Kajaria"
                className="w-16 h-6 rounded-lg"
              />
              <img
                src="images/gresbond-logo.png"
                alt="Gresbond"
                className="filter dark-blue h-6 "
              />
              <img
                src="images/kerovit_logo.png"
                alt="Kerovit"
                className="filter dark-blue h-6 m-1"
              />
            </div>
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">
                {user?.username?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-hidden">
          <div className="h-full p-4 sm:p-6 lg:p-8 overflow-y-auto">
            <div className="max-w-7xl mx-auto h-full">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeLayout;
